import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Star } from 'lucide-react';
import { CreatePropertyDto } from '../../../handling_data/dtos/PropertyDto';
import { StepRef } from './BasicInfoStep';

interface FeaturesStepProps {
    t: any;
    formData: CreatePropertyDto;
}

const FeaturesStep = forwardRef<StepRef, FeaturesStepProps>(({ t, formData }, ref) => {
    const [localData, setLocalData] = useState({
        rooms: formData.rooms,
        bathrooms: formData.bathrooms,
        area: formData.area,
        additional_features: [...(formData.additional_features || [])]
    });

    // Sync local state when formData changes (essential for edit flow)
    useEffect(() => {
        setLocalData({
            rooms: formData.rooms,
            bathrooms: formData.bathrooms,
            area: formData.area,
            additional_features: [...(formData.additional_features || [])]
        });
    }, [formData.rooms, formData.bathrooms, formData.area, formData.additional_features]);

    useImperativeHandle(ref, () => ({
        getData: () => localData
    }));

    const availableFeatures = [
        { id: 'pool', label: 'مسبح', labelEn: 'Swimming Pool' },
        { id: 'parking', label: 'كراج', labelEn: 'Garage' },
        { id: 'ac', label: 'تكييف', labelEn: 'Air Conditioning' },
        { id: 'terrace', label: 'تراس', labelEn: 'Terrace' },
        { id: 'wifi', label: 'إنترنت', labelEn: 'Internet/WiFi' },
        { id: 'furnished', label: 'مفروش', labelEn: 'Furnished' }
    ];

    const toggleFeature = (label: string) => {
        setLocalData(prev => {
            const currentFeatures = prev.additional_features || [];
            const isSelected = currentFeatures.includes(label);
            const newFeatures = isSelected
                ? currentFeatures.filter(f => f !== label)
                : [...currentFeatures, label];
            return { ...prev, additional_features: newFeatures };
        });
    };

    const updateField = (field: keyof typeof localData, value: any) => {
        setLocalData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <div className="space-y-10 animate-fade-in">
            <div className="text-center">
                <Star className="w-12 h-12 mx-auto text-primary mb-4 opacity-80" />
                <h3 className="text-xl font-bold text-gray-800">{t('property_form.features.title')}</h3>
                <p className="text-gray-500 text-sm">{t('property_form.features.subtitle') || 'يرجى إدخال المواصفات الأساسية واختيار المميزات الإضافية'}</p>
            </div>

            {/* Core Metrics (Required) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto bg-gray-50 p-8 rounded-3xl border border-gray-100 shadow-inner">
                <div className="space-y-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">{t('property_form.features.bedrooms')} / غرف المبيت</label>
                    <input
                        type="number"
                        className="w-full px-4 py-4 rounded-2xl border-2 border-white bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all text-center text-xl font-bold shadow-sm"
                        placeholder="0"
                        value={localData.rooms}
                        onChange={(e) => updateField('rooms', e.target.value)}
                    />
                </div>
                <div className="space-y-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">{t('property_form.features.bathrooms') || 'حمامات'}</label>
                    <input
                        type="number"
                        className="w-full px-4 py-4 rounded-2xl border-2 border-white bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all text-center text-xl font-bold shadow-sm"
                        placeholder="0"
                        value={localData.bathrooms}
                        onChange={(e) => updateField('bathrooms', e.target.value)}
                    />
                </div>
                <div className="space-y-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">{t('property_form.features.area') || 'المساحة (م²)'}</label>
                    <input
                        type="number"
                        className="w-full px-4 py-4 rounded-2xl border-2 border-white bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all text-center text-xl font-bold shadow-sm"
                        placeholder="0"
                        value={localData.area}
                        onChange={(e) => updateField('area', e.target.value)}
                    />
                </div>
            </div>

            {/* Additional Features List */}
            <div className="space-y-4">
                <h4 className="text-center font-bold text-gray-700">{t('property_form.additional_features') || 'مميزات إضافية'}</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {availableFeatures.map((feature) => {
                        const isSelected = localData.additional_features?.includes(feature.label);
                        return (
                            <button
                                key={feature.id}
                                type="button"
                                onClick={() => toggleFeature(feature.label)}
                                className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${isSelected
                                    ? 'border-primary bg-primary/5 text-primary shadow-md shadow-primary/5'
                                    : 'border-white bg-gray-50 text-gray-400 hover:border-gray-200 hover:bg-white'
                                    }`}
                            >
                                <span className="text-xs font-bold">{feature.label}</span>
                                <span className="text-[8px] opacity-60 uppercase tracking-wider">{feature.labelEn}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

export default FeaturesStep;
